import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import { Button } from '../ui/button';
import Input from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SearchableSelect from '../ui/SearchableSelect';
import { apiService } from '../../services/api';
import { WorkOrder, StatusDto, PriorityDto, User, BulkEditFormData, BulkUpdateWorkOrderDto } from '../../types';
import { toast } from 'react-hot-toast';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedWorkOrders: WorkOrder[];
  onSuccess: () => void;
}

const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  selectedWorkOrders,
  onSuccess
}) => {
  const [formData, setFormData] = useState<BulkEditFormData>({
    updateStatus: false,
    statusId: undefined,
    updateAssignedUser: false,
    assignedUserId: undefined,
    updatePriority: false,
    priorityId: undefined
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch statuses, priorities, and users
  const { data: statusesResponse } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => apiService.getStatuses(),
    enabled: isOpen
  });

  const { data: prioritiesResponse } = useQuery({
    queryKey: ['priorities'],
    queryFn: () => apiService.getPriorities(),
    enabled: isOpen
  });

  const { data: usersResponse } = useQuery({
    queryKey: ['company-users'],
    queryFn: () => apiService.getCompanyUsers(),
    enabled: isOpen
  });

  const statuses = statusesResponse?.data || [];
  const priorities = prioritiesResponse?.data || [];
  const users = usersResponse?.data || [];


  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: (payload: BulkUpdateWorkOrderDto) => apiService.bulkUpdateWorkOrders(payload),
    onSuccess: (response) => {
      if (response.success) {
        const { successCount, failureCount, errors } = response.data;
        
        if (failureCount === 0) {
          toast.success(`Successfully updated ${successCount} work orders`);
        } else {
          toast.success(`Updated ${successCount} of ${successCount + failureCount} work orders`);
          if (errors.length > 0) {
            console.error('Bulk update errors:', errors);
            // Could show error details in a separate modal
          }
        }
        
        onSuccess();
        onClose();
      } else {
        toast.error(response.message || 'Failed to update work orders');
      }
    },
    onError: (error) => {
      console.error('Bulk update error:', error);
      toast.error('Failed to update work orders');
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that at least one field is selected for update
    if (!formData.updateStatus && !formData.updateAssignedUser && !formData.updatePriority) {
      toast.error('Please select at least one field to update');
      return;
    }

    setIsSubmitting(true);

    try {
      const updates: any = {};
      
      if (formData.updateStatus && formData.statusId) {
        updates.statusId = formData.statusId;
      }
      
      if (formData.updateAssignedUser && formData.assignedUserId) {
        updates.assignedUserId = formData.assignedUserId;
      }
      
      if (formData.updatePriority && formData.priorityId) {
        updates.priorityId = formData.priorityId;
      }

      const payload: BulkUpdateWorkOrderDto = {
        workOrderIds: selectedWorkOrders.map(wo => wo.id),
        updates
      };

      await bulkUpdateMutation.mutateAsync(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (field: keyof BulkEditFormData, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked,
      // Clear the related value when unchecking
      ...(field === 'updateStatus' && !checked && { statusId: undefined }),
      ...(field === 'updateAssignedUser' && !checked && { assignedUserId: undefined }),
      ...(field === 'updatePriority' && !checked && { priorityId: undefined })
    }));
  };

  const handleSelectChange = (field: keyof BulkEditFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value === '' ? undefined : (field.includes('Id') ? parseInt(value) : value)
    }));
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        updateStatus: false,
        statusId: undefined,
        updateAssignedUser: false,
        assignedUserId: undefined,
        updatePriority: false,
        priorityId: undefined
      });
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Edit Selected Work Orders ({selectedWorkOrders.length} items)
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Selected Work Orders Summary */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Selected Work Orders</h3>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-40 overflow-y-auto">
            <div className="space-y-2">
              {selectedWorkOrders.map((wo) => (
                <div key={wo.id} className="flex justify-between items-center text-sm">
                  <span className="font-medium">{wo.workOrderNumber}</span>
                  <span className="text-gray-600 dark:text-gray-300">{wo.clientName}</span>
                  <span className="text-gray-500 dark:text-gray-400">{wo.statusName}</span>
                  <span className="text-gray-500 dark:text-gray-400">{wo.assignedUserName}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Update Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Status Update */}
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="updateStatus"
                checked={formData.updateStatus}
                onCheckedChange={(checked) => handleCheckboxChange('updateStatus', checked)}
                disabled={isSubmitting}
              />
              <Label htmlFor="updateStatus" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Update Status
              </Label>
            </div>
            
            {formData.updateStatus && (
              <div className="ml-6">
                <Select
                  value={formData.statusId?.toString() || ''}
                  onValueChange={(value) => handleSelectChange('statusId', value)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger onClick={(e) => e.stopPropagation()}>
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent onClick={(e) => e.stopPropagation()}>
                    {statuses.map((status: StatusDto) => (
                      <SelectItem key={status.id} value={status.id.toString()}>
                        {status.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Assigned User Update */}
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="updateAssignedUser"
                checked={formData.updateAssignedUser}
                onCheckedChange={(checked) => handleCheckboxChange('updateAssignedUser', checked)}
                disabled={isSubmitting}
              />
              <Label htmlFor="updateAssignedUser" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Update Assigned User
              </Label>
            </div>
            
            {formData.updateAssignedUser && (
              <div className="ml-6" onClick={(e) => e.stopPropagation()}>
                <SearchableSelect
                  options={users
                    .filter((user: User) => !!user.id)
                    .map((user: User) => ({
                      value: user.id,
                      label: `${user.firstName} ${user.lastName}`.trim() || user.email || 'Unknown user',
                    }))}
                  value={formData.assignedUserId || ''}
                  onChange={(value) => handleSelectChange('assignedUserId', String(value || ''))}
                  placeholder="Select User"
                  searchPlaceholder="Search users..."
                  disabled={isSubmitting}
                  aria-label="Select assigned user"
                />
              </div>
            )}
          </div>

          {/* Priority Update */}
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="updatePriority"
                checked={formData.updatePriority}
                onCheckedChange={(checked) => handleCheckboxChange('updatePriority', checked)}
                disabled={isSubmitting}
              />
              <Label htmlFor="updatePriority" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Update Priority
              </Label>
            </div>
            
            {formData.updatePriority && (
              <div className="ml-6">
                <Select
                  value={formData.priorityId?.toString() || ''}
                  onValueChange={(value) => handleSelectChange('priorityId', value)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger onClick={(e) => e.stopPropagation()}>
                    <SelectValue placeholder="Select Priority" />
                  </SelectTrigger>
                  <SelectContent onClick={(e) => e.stopPropagation()}>
                    {priorities.map((priority: PriorityDto) => (
                      <SelectItem key={priority.id} value={priority.id.toString()}>
                        {priority.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || (!formData.updateStatus && !formData.updateAssignedUser && !formData.updatePriority)}
            >
              {isSubmitting ? 'Updating...' : 'Apply Updates'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default BulkEditModal;
